<script setup lang="ts">
import { computed, watch, ref } from 'vue';
import ChevronRightIcon from 'vue-material-design-icons/ChevronRight.vue';
import ArrowLeftIcon from 'vue-material-design-icons/ArrowLeft.vue';
import { useRoute } from '#app/composables/router';

const route = useRoute();

interface MenuTagChild {
  id: number;
  name: string;
  description?: string | null;
  count: number;
}

interface MenuTagGroup {
  id: number;
  name: string;
  recipe_tags: MenuTagChild[];
}

const props = defineProps<{
  menuTags: MenuTagGroup[];
  currentTagId?: number;
}>();

const emit = defineEmits<{
  (e: 'change-tag', tagId?: number): void;
}>();

function changeTag(tagId?: number) {
  emit('change-tag', tagId);
}

// NOTE: 「すべて表示」機能
const isExpanded = ref(false);

// NOTE: 検索条件が変更された場合は展開を解除
watch(
  () => route.query,
  () => {
    if (isExpanded.value) {
      isExpanded.value = false;
    }
  },
);

// 最初のタググループ
const firstGroup = computed(() => props.menuTags[0]);

// 最初のグループのタグ: 初期表示分（4件まで）
const firstGroupInitialTags = computed(() => {
  return firstGroup.value?.recipe_tags.slice(0, 4) ?? [];
});

// 最初のグループのタグ: 省略分（5件目以降）
const firstGroupOmittedTags = computed(() => {
  return firstGroup.value?.recipe_tags.slice(4) ?? [];
});

// 2番目以降のタググループ
const restGroups = computed(() => props.menuTags.slice(1));

// 省略分がある場合にボタンを表示
const shouldShowExpandButton = computed(() => {
  if (isExpanded.value) {
    return false;
  }
  return firstGroupOmittedTags.value.length > 0 || restGroups.value.length > 0;
});
</script>

<template>
  <div>
    <button
      v-if="currentTagId"
      class="flex mb-3 py-2.5 px-3 bg-cream-200 border border-cream-400 w-full rounded shadow-sm"
      data-testid="back-button"
      @click="changeTag()"
    >
      <ArrowLeftIcon
        :size="20"
        class="mr-1 relative bottom-[1px] text-brown-300"
      />
      <span class="text-gray-500">すべての献立</span>
    </button>

    <!-- 最初のタググループ -->
    <div v-if="firstGroup" class="mb-2">
      <p
        class="mb-3 flex items-center gap-2 py-2 px-3 bg-white border-0 rounded-none shadow-none border-b border-cream-400"
      >
        <span class="font-bold text-pink-400 max-md:grow">{{
          firstGroup.name
        }}</span>
      </p>

      <!-- 初期表示分（4件まで） -->
      <ul class="grid grid-cols-2 gap-2 px-2">
        <li v-for="tag in firstGroupInitialTags" :key="tag.id">
          <button
            data-testid="tag-button"
            class="w-full h-full flex gap-2 hover:underline py-2 px-3 min-h-[22px] items-center bg-gray-100 border border-gray-200 rounded shadow-sm"
            @click="changeTag(tag.id)"
          >
            <span class="max-md:grow text-gray-500">{{ tag.name }}</span>
            <ChevronRightIcon
              :size="24"
              class="relative top-[1px] text-brown-300"
            />
          </button>
        </li>
      </ul>

      <!-- 省略分（5件目以降）: 展開後のみ表示 -->
      <ul
        v-if="isExpanded && firstGroupOmittedTags.length > 0"
        class="grid grid-cols-2 gap-2 px-2 mt-2"
      >
        <li v-for="tag in firstGroupOmittedTags" :key="tag.id">
          <button
            data-testid="tag-button"
            class="w-full h-full flex gap-2 hover:underline py-2 px-3 min-h-[22px] items-center bg-gray-100 border border-gray-200 rounded shadow-sm"
            @click="changeTag(tag.id)"
          >
            <span class="max-md:grow text-gray-500">{{ tag.name }}</span>
            <ChevronRightIcon
              :size="24"
              class="relative top-[1px] text-brown-300"
            />
          </button>
        </li>
      </ul>
    </div>

    <!-- 2番目以降のタググループ: 展開後のみ表示 -->
    <ul v-if="isExpanded">
      <li v-for="menuTag in restGroups" :key="menuTag.id" class="mb-2">
        <p
          class="mb-3 flex items-center gap-2 py-2 px-3 bg-white border-0 rounded-none shadow-none border-b border-cream-400"
        >
          <span class="font-bold text-pink-400 max-md:grow">{{
            menuTag.name
          }}</span>
        </p>

        <ul class="grid grid-cols-2 gap-2 px-2">
          <li v-for="child in menuTag.recipe_tags" :key="child.id">
            <button
              data-testid="tag-button"
              class="w-full h-full flex gap-2 hover:underline py-2 px-3 min-h-[22px] items-center bg-gray-100 border border-gray-200 rounded shadow-sm"
              @click="changeTag(child.id)"
            >
              <span class="max-md:grow text-gray-500">{{ child.name }}</span>
              <ChevronRightIcon
                :size="24"
                class="relative top-[1px] text-brown-300"
              />
            </button>
          </li>
        </ul>
      </li>
    </ul>

    <div v-if="shouldShowExpandButton" class="px-2 pb-2">
      <button
        class="w-full py-2 px-3 text-center text-lg text-gray-600 hover:bg-gray-50 border border-gray-300 rounded shadow"
        data-testid="expand-button"
        @click="isExpanded = true"
      >
        すべての検索条件を表示
      </button>
    </div>
  </div>
</template>
